import React from 'react';
import { CheckIcon, PhoneCallIcon, CalendarCheckIcon, MessageCircleIcon } from 'lucide-react';
import { Badge } from '../Badge';
import { Button } from '../Button';
function scrollToContact() {
  document.querySelector('#contact')?.scrollIntoView({
    behavior: 'smooth'
  });
}
const FEATURES: string[] = ['かかってきた電話に、AI受付スタッフがいつでも応対', '24時間365日、予約をそのまま受け付け', 'よくある問い合わせには、待たせず即座に回答', '取り次ぎや伝言もおまかせ。あなたは本業に集中'];
type Activity = {
  icon: React.ReactNode;
  label: string;
  text: string;
};
const ACTIVITY: Activity[] = [{
  icon: <PhoneCallIcon className="h-5 w-5" aria-hidden="true" data-id="element-270" data-mp="iaup91c0ymcz" />,
  label: '電話応対',
  text: '「お電話ありがとうございます。ご用件をお伺いします。」'
}, {
  icon: <CalendarCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-271" data-mp="r2ob93abop9y" />,
  label: '予約受付',
  text: '木曜 14:00 のご予約を承りました。'
}, {
  icon: <MessageCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-272" data-mp="3qpd69sjy1c8q" />,
  label: '問い合わせ',
  text: '営業時間は平日 9:00〜18:00です。即時にご回答しました。'
}];
export function SolutionSection() {
  return <section id="ai-staff" className="w-full scroll-mt-24 bg-white" data-id="element-273" data-mp="32l7ibjxo3kpc">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-2" data-id="element-274" data-mp="2xlux13mg2lov">
        {/* Copy column */}
        <div data-id="element-275" data-mp="2su34wonn47wm">
          <Badge variant="sage" data-id="element-276" data-mp="2sttmpn8u751h">OptiDSA の AI受付スタッフ</Badge>
          <h2 className="mt-4 text-2xl font-bold leading-snug text-text sm:text-3xl" data-id="element-277" data-mp="2nwbgw5q495xp">
            電話も予約も問い合わせも、
            <br className="hidden sm:block" data-id="element-278" data-mp="2ix74z7bt4u3o" />
            「AI受付スタッフ」におまかせ。
          </h2>
          <p className="mt-4 text-base leading-relaxed text-text-muted" data-id="element-279" data-mp="2euu1kpkj98a2">
            むずかしい設定や操作は必要ありません。あなたの会社の“もう一人の受付担当”として、
            AI受付スタッフが日々の対応を引き受けます。起こることはシンプルです——
            電話に出られ、予約が入り、問い合わせに即答できる。それだけです。
          </p>

          <ul className="mt-8 space-y-3" data-id="element-280" data-mp="24yh55jxlsgbm">
            {FEATURES.map(f => <li key={f} className="flex items-start gap-3" data-id="element-281" data-mp="83uk7tak9o69">
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage-green/15 text-sage-green" data-id="element-282" data-mp="35t0d65b9ktq">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-283" data-mp="1xl9a85viczv4" />
                </span>
                <span className="text-base leading-relaxed text-text" data-id="element-284" data-mp="22dsypxvqhfep">{f}</span>
              </li>)}
          </ul>

          <div className="mt-10" data-id="element-285" data-mp="1npog0twol3vx">
            <Button variant="primary" size="lg" onClick={scrollToContact} data-id="element-286" data-mp="1smy3w54f97hn">
              お問い合わせ
            </Button>
          </div>
        </div>

        {/* Visual panel */}
        <div className="rounded-lg bg-deep-navy p-6 shadow-lg sm:p-8" aria-hidden="true" data-id="element-287" data-mp="2gafvo0nos5km">
          <div className="flex items-center gap-3 border-b border-white/15 pb-4" data-id="element-288" data-mp="26ecvh0ceah0b">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sky-blue/25 text-white" data-id="element-289" data-mp="26gfntr10sm87">
              <PhoneCallIcon className="h-5 w-5" data-id="element-290" data-mp="uzft6or79vb0" />
            </span>
            <div data-id="element-291" data-mp="v1agg9py1zi9">
              <p className="text-sm font-bold text-white" data-id="element-292" data-mp="zy988lm4xuda">AI受付スタッフ</p>
              <p className="flex items-center gap-1.5 text-xs text-white/70" data-id="element-293" data-mp="zzkb8j7ray3c">
                <span className="inline-block h-2 w-2 rounded-full bg-sage-green" data-id="element-294" data-mp="3e4qmt75u736k" />
                対応中・24時間オンライン
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-4" data-id="element-295" data-mp="3n6gdubvt403t">
            {ACTIVITY.map(a => <div key={a.label} className="rounded-md bg-white/10 p-4" data-id="element-296" data-mp="3n4dntrunm6z3">
                <div className="flex items-center gap-2 text-sky-blue" data-id="element-297" data-mp="3j5wgb77bmuh5">
                  {a.icon}
                  <span className="text-xs font-semibold uppercase tracking-wide" data-id="element-298" data-mp="2vfsdh21yiyr7">
                    {a.label}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-white/90" data-id="element-299" data-mp="34842qklc4g5l">
                  {a.text}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}